import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, X, Repeat, MapPin, Calendar, Building2, Ban, FileText, Download, Copy, Check, Mail, MessageSquare, Send, UserCircle, AlertTriangle, ClipboardCheck, Layers, Inbox, Sparkles } from "lucide-react";
import { tzAbbr, fmtLocal } from "@/lib/time";

import { useAuth, api } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ArtworkPanel } from "@/components/noxn/ArtworkPanel";
import { CampaignTaskList } from "@/pages/TasksBoard";
import { PortalShell } from "@/components/account/PortalShell";

const COLUMNS = [
    { key: "new", label: "New", accent: "border-white/20 text-white" },
    { key: "approved", label: "Approved", accent: "border-sky-400/50 text-sky-300" },
    { key: "live", label: "Live", accent: "border-emerald-400/60 text-emerald-300" },
    { key: "done", label: "Done", accent: "border-slate-500/60 text-slate-300" },
    { key: "cancelled", label: "Cancelled", accent: "border-[#FF5A00]/60 text-[#FF5A00]" },
];

const money = (aud) => aud ? `$${Number(aud).toLocaleString("en-AU")}` : "—";

export default function Pipeline() {
    usePageMeta({ title: "Pipeline · NOXN CRM", description: "Move campaigns through the pipeline." });
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null); // campaign detail
    const [dragOver, setDragOver] = useState(null);
    const [access, setAccess] = useState(null);
    const [submissions, setSubmissions] = useState(null);
    const canManage = !!access?.permissions?.includes("campaigns.manage");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/campaigns");
            setCampaigns(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load campaigns.");
        } finally { setLoading(false); }
    }, []);

    const loadSubmissions = useCallback(async () => {
        try {
            const { data } = await api.get("/campaigns/submissions");
            setSubmissions(data);
        } catch (e) { setSubmissions([]); }
    }, []);

    useEffect(() => {
        api.get("/admin/my-access").then(({ data }) => setAccess(data)).catch(() => setAccess({ permissions: [] }));
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (canManage) loadSubmissions(); }, [canManage, loadSubmissions]);

    const claimSubmission = async (id) => {
        try {
            await api.post(`/campaigns/${id}/claim`);
            toast.success("Claimed — it's yours now.");
            setSubmissions((rows) => (rows || []).filter((r) => r.id !== id));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not claim this submission.");
            loadSubmissions();
        }
    };

    const byStatus = useMemo(() => {
        const m = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
        for (const c of campaigns) {
            (m[c.pipeline_status] || m.new).push(c);
        }
        return m;
    }, [campaigns]);

    const updateStatus = async (id, next) => {
        const prev = campaigns.find((c) => c.id === id)?.pipeline_status;
        if (prev === next) return;
        // Optimistic update
        setCampaigns((rows) => rows.map((c) => c.id === id ? { ...c, pipeline_status: next } : c));
        try {
            await api.patch(`/campaigns/${id}`, { pipeline_status: next });
            toast.success(`Moved to ${next}.`);
        } catch (e) {
            setCampaigns((rows) => rows.map((c) => c.id === id ? { ...c, pipeline_status: prev } : c));
            toast.error(e?.response?.data?.detail || "Could not update.");
        }
    };

    const onDrop = (statusKey) => (e) => {
        e.preventDefault();
        setDragOver(null);
        const id = e.dataTransfer.getData("text/plain");
        if (id) updateStatus(id, statusKey);
    };

    const openDetail = async (c) => {
        setSelected({ ...c, _loading: true });
        try {
            const { data } = await api.get(`/campaigns/${c.id}`);
            setSelected(data);
        } catch (e) {
            toast.error("Could not load campaign detail.");
            setSelected(null);
        }
    };

    // Deep-link support: /dashboard/pipeline?campaign=<id> auto-opens the detail modal.
    useEffect(() => {
        const cid = searchParams.get("campaign");
        if (cid && campaigns.length && !selected) {
            const match = campaigns.find((c) => c.id === cid);
            if (match) openDetail(match);
            setSearchParams((p) => { p.delete("campaign"); return p; }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaigns]);

    return (
        <PortalShell title="Pipeline" mainClassName="p-0">
        <div className="min-h-screen w-full relative">
            <div className="absolute inset-0 -z-10 grid-noise opacity-25" />

            <main className="mx-auto max-w-[1500px] px-6 lg:px-10 py-10">
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, ease: [0.16,1,0.3,1] }}>
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-4 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" /> Pipeline · Campaigns
                    </div>
                    <h1 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl leading-[0.95]">
                        Move the work.
                    </h1>
                    <p className="mt-4 text-slate-400 text-sm max-w-xl">
                        Drag a card between columns, or open it to swap screens without breaking bookings.
                    </p>
                </motion.div>

                {loading ? (
                    <div className="flex items-center justify-center py-24">
                        <Loader2 className="animate-spin text-[#FF5A00]" size={22} />
                    </div>
                ) : (
                    <>
                    {canManage && (
                        <SubmissionsPanel submissions={submissions} onClaim={claimSubmission} onOpen={openDetail} />
                    )}
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="pipeline-board">
                        {COLUMNS.map((col) => (
                            <div
                                key={col.key}
                                data-testid={`pipeline-col-${col.key}`}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(col.key); }}
                                onDragLeave={() => setDragOver((s) => (s === col.key ? null : s))}
                                onDrop={onDrop(col.key)}
                                className={`border ${dragOver === col.key ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-white/10 bg-[#1B2E55]/40"} min-h-[240px] p-4 transition-colors`}
                            >
                                <div className={`flex items-center justify-between mb-4 border-b pb-3 ${col.accent}`}>
                                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em]">/ {col.label}</div>
                                    <div className="text-xs font-mono-tech text-slate-400">{byStatus[col.key].length}</div>
                                </div>
                                <div className="space-y-3">
                                    {byStatus[col.key].map((c) => (
                                        <PipelineCard key={c.id} c={c} onOpen={openDetail} onMove={updateStatus} canManage={canManage} />
                                    ))}
                                    {byStatus[col.key].length === 0 && (
                                        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-600 text-center py-6">— Empty —</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    </>
                )}
            </main>

            {/* Detail modal */}
            <AnimatePresence>
                {selected && (
                    <DetailModal
                        campaign={selected}
                        onClose={() => setSelected(null)}
                        onUpdated={async () => { await load(); if (selected?.id) { const { data } = await api.get(`/campaigns/${selected.id}`); setSelected(data); } }}
                        onStatusChange={updateStatus}
                        canManage={canManage}
                    />
                )}
            </AnimatePresence>
        </div>
        </PortalShell>
    );
}

const SubmissionsPanel = ({ submissions, onClaim, onOpen }) => {
    const [claiming, setClaiming] = useState(null);
    if (submissions === null) {
        return (
            <div className="mt-8 flex items-center gap-2 text-slate-500 text-xs"><Loader2 size={13} className="animate-spin" /> Loading submissions…</div>
        );
    }
    return (
        <div className="mt-8 border border-[#FF5A00]/40 bg-[#1B2E55]/50 p-5" data-testid="pipeline-submissions-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#FF5A00] flex items-center gap-2">
                    <Inbox size={13} /> New Campaign Submissions
                </div>
                <span className="text-xs font-mono-tech text-slate-400" data-testid="pipeline-submissions-count">{submissions.length} waiting</span>
            </div>
            {submissions.length === 0 ? (
                <div className="text-xs text-slate-500 font-mono-tech uppercase tracking-widest py-3" data-testid="pipeline-submissions-empty">
                    All caught up — no unclaimed submissions right now.
                </div>
            ) : (
                <div className="space-y-2" data-testid="pipeline-submissions-list">
                    {submissions.map((c) => (
                        <div key={c.id} className="border border-white/10 bg-[#0F1E3B] p-3 flex flex-wrap items-center gap-3" data-testid={`pipeline-submission-${c.id}`}>
                            <button onClick={() => onOpen(c)} className="flex-1 min-w-[180px] text-left">
                                <div className="text-white text-sm font-medium flex items-center gap-1.5">
                                    <Building2 size={12} className="text-[#FF5A00]" /> {c.business_name || "(no name)"}
                                </div>
                                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mt-0.5">
                                    {c.contact_name} · {(c.cities && c.cities[0]) || "—"} · {money(c.budget_amount_aud)}
                                </div>
                            </button>
                            <button
                                onClick={async () => { setClaiming(c.id); await onClaim(c.id); setClaiming(null); }}
                                disabled={claiming === c.id}
                                data-testid={`pipeline-submission-${c.id}-action`}
                                className="inline-flex items-center gap-1.5 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 py-2 text-[10px] font-semibold uppercase tracking-widest shrink-0"
                            >
                                {claiming === c.id ? <Loader2 size={12} className="animate-spin" /> : <><Sparkles size={12} /> Action</>}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const PipelineCard = ({ c, onOpen, onMove, canManage }) => {
    const canForward = canManage && c.pipeline_status !== "done" && c.pipeline_status !== "cancelled";
    const nextStatus = { new: "approved", approved: "live", live: "done" }[c.pipeline_status];
    return (
        <div
            draggable={canManage}
            onDragStart={(e) => { if (!canManage) return; e.dataTransfer.setData("text/plain", c.id); e.dataTransfer.effectAllowed = "move"; }}
            onClick={() => onOpen(c)}
            data-testid={`pipeline-card-${c.id}`}
            className="border border-white/10 bg-[#0F1E3B] hover:border-[#FF5A00]/60 transition-colors p-3 cursor-pointer group"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-white text-sm font-medium truncate flex items-center gap-1.5">
                        <Building2 size={12} className="text-[#FF5A00]" /> {c.business_name || "(no name)"}
                    </div>
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mt-0.5 truncate">
                        {c.contact_name} · {c.form_category === "cat1" ? "cat 1" : "cat 2"}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="text-xs font-mono-tech text-white">{money(c.budget_amount_aud)}</div>
                </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] font-mono-tech text-slate-400">
                <div className="flex items-center gap-1"><Calendar size={10} className="text-slate-500" /> {c.launch_date || "—"}</div>
                <div className="flex items-center gap-1 truncate"><MapPin size={10} className="text-slate-500" /> {(c.cities && c.cities[0]) || "—"}</div>
            </div>
            <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono-tech border border-emerald-400/40 text-emerald-300">
                        {c.screen_counts?.confirmed || 0} confirmed
                    </span>
                    {(c.screen_counts?.waitlist || 0) > 0 && (
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono-tech border border-[#FF5A00]/50 text-[#FF5A00]">
                            {c.screen_counts.waitlist} waitlist
                        </span>
                    )}
                    {c.missing_proof && (
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono-tech border border-red-400/50 text-red-300" data-testid={`pipeline-missing-proof-${c.id}`}>
                            proof missing
                        </span>
                    )}
                </div>
                {canForward && nextStatus && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onMove(c.id, nextStatus); }}
                        data-testid={`pipeline-card-${c.id}-forward`}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 hover:text-[#FF5A00] flex items-center gap-1"
                        title={`Move to ${nextStatus}`}
                    >
                        {nextStatus} <ArrowRight size={10} />
                    </button>
                )}
            </div>
        </div>
    );
};

const DetailModal = ({ campaign, onClose, onUpdated, onStatusChange, canManage }) => {
    const isLoading = !!campaign._loading;
    return (
        <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="pipeline-detail-modal"
        >
            <motion.div
                onClick={(e) => e.stopPropagation()}
                initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                className="w-full max-w-2xl bg-[#0F1E3B] border border-white/10 max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0F1E3B] z-10">
                    <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Campaign</div>
                        <div className="text-white font-display font-black text-xl truncate max-w-md">{campaign.business_name || "(no name)"}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="pipeline-detail-close">
                        <X size={18} />
                    </button>
                </div>
                {isLoading ? (
                    <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-[#FF5A00]" size={22} /></div>
                ) : (
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <Info k="Contact" v={`${campaign.contact_name || "—"}`} />
                            <Info k="Email" v={campaign.email} />
                            <Info k="Launch date" v={campaign.launch_date} />
                            <Info k="Budget" v={money(campaign.budget_amount_aud)} />
                            {campaign.referral?.discount_cents ? (
                                <Info k="Referral" v={`−$${(campaign.referral.discount_cents / 100).toFixed(0)} (${campaign.referral.code || "—"})`} />
                            ) : null}
                            {campaign.client_relationship?.priority ? (
                                <Info k="Client" v={`★ Priority — ${campaign.client_relationship.tenure_label} with NOXN`} />
                            ) : null}
                            <Info k="Category" v={campaign.form_category === "cat1" ? "cat 1" : "cat 2"} />
                            <Info k="Goal" v={campaign.campaign_goal} />
                        </div>

                        <OwnerRow campaign={campaign} onUpdated={onUpdated} />

                        {canManage ? (
                            <div className="flex flex-wrap gap-2">
                                {COLUMNS.map((s) => (
                                    <button
                                        key={s.key}
                                        onClick={() => onStatusChange(campaign.id, s.key)}
                                        data-testid={`pipeline-detail-set-${s.key}`}
                                        className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border transition-colors ${
                                            campaign.pipeline_status === s.key
                                                ? "border-[#FF5A00] bg-[#FF5A00]/10 text-white"
                                                : "border-white/15 text-slate-400 hover:text-white hover:border-white/40"
                                        }`}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500" data-testid="pipeline-detail-readonly-status">
                                Status: <span className="text-white">{campaign.pipeline_status}</span> — view only, you don&apos;t have campaign-management permission.
                            </div>
                        )}

                        <div>
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">/ Screens</div>
                            {campaign.bookings?.length ? (
                                <div className="space-y-2" data-testid="pipeline-bookings-list">
                                    {campaign.bookings.map((b) => (
                                        <BookingRow key={b.id} campaignId={campaign.id} booking={b} onUpdated={onUpdated} canManage={canManage} />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-xs text-slate-500 font-mono-tech" data-testid="pipeline-bookings-empty">
                                    No bookings attached yet.
                                </div>
                            )}
                        </div>

                        <ContractPanel campaignId={campaign.id} />

                        <ArtworkPanel campaignId={campaign.id} canUpload={false} staffReview />

                        <CampaignTaskList campaignId={campaign.id} />

                        <QuoteVersions campaignId={campaign.id} />

                        <ProofPanel campaign={campaign} onUpdated={onUpdated} />

                        <CampaignThread campaignId={campaign.id} />
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};

const Info = ({ k, v }) => (
    <div>
        <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">{k}</div>
        <div className="text-white text-sm mt-1 truncate">{v || "—"}</div>
    </div>
);

const BookingRow = ({ campaignId, booking, onUpdated, canManage }) => {
    const [swapping, setSwapping] = useState(false);
    const [candidates, setCandidates] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadCandidates = async () => {
        setSwapping(true);
        setBusy(true);
        try {
            const { data } = await api.get("/screens/availability/check", {
                params: { start: booking.start_date, end: booking.end_date },
            });
            setCandidates(data.filter((s) => s.available && s.screen_id !== booking.screen_id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load candidates.");
        } finally { setBusy(false); }
    };

    const swap = async (screenId) => {
        setBusy(true);
        try {
            await api.post(`/campaigns/${campaignId}/screens/swap`, {
                booking_id: booking.id,
                new_screen_id: screenId,
            });
            toast.success("Screen swapped.");
            setSwapping(false);
            await onUpdated();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Swap failed.");
        } finally { setBusy(false); }
    };

    const isReleasable = booking.status !== "released";
    const statusColor = {
        confirmed: "text-emerald-300 border-emerald-400/40",
        hold: "text-amber-300 border-amber-400/40",
        waitlist: "text-[#FF5A00] border-[#FF5A00]/50",
        released: "text-slate-500 border-slate-600",
        reserved: "text-sky-300 border-sky-400/40",
    }[booking.status] || "text-slate-400 border-white/10";

    return (
        <div className="border border-white/10 p-3" data-testid={`pipeline-booking-${booking.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-white text-sm truncate">{booking.screen_name}</div>
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500" title={booking.timezone || ""}>
                        {booking.city} · {booking.start_date} → {booking.end_date}{booking.timezone ? ` · ${tzAbbr(booking.timezone)} local` : ""}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono-tech uppercase tracking-widest border ${statusColor}`}>
                        {booking.status}
                    </span>
                    {isReleasable && canManage && (
                        <button
                            onClick={loadCandidates}
                            disabled={busy}
                            data-testid={`pipeline-booking-${booking.id}-swap`}
                            className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 hover:text-[#FF5A00] inline-flex items-center gap-1"
                        >
                            <Repeat size={10} /> Swap
                        </button>
                    )}
                </div>
            </div>
            {swapping && (
                <div className="mt-3 border-t border-white/10 pt-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-400">Available for this window</div>
                        <button onClick={() => { setSwapping(false); setCandidates(null); }} className="text-slate-500 hover:text-white" data-testid={`pipeline-booking-${booking.id}-swap-close`}>
                            <X size={12} />
                        </button>
                    </div>
                    {busy && !candidates ? (
                        <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
                    ) : candidates && candidates.length ? (
                        <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                            {candidates.map((c) => (
                                <li key={c.screen_id}>
                                    <button
                                        onClick={() => swap(c.screen_id)}
                                        disabled={busy}
                                        data-testid={`pipeline-booking-${booking.id}-swap-to-${c.screen_id}`}
                                        className="w-full text-left flex items-center justify-between border border-white/10 hover:border-[#FF5A00] px-3 py-2 text-sm transition-colors"
                                    >
                                        <span className="text-white truncate">{c.name}</span>
                                        <span className="text-[10px] font-mono-tech uppercase tracking-widest text-emerald-400 shrink-0"><Repeat size={10} className="inline mr-1" />Use</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-xs text-slate-500 flex items-center gap-2"><Ban size={12} /> No other screens are free for this window.</div>
                    )}
                </div>
            )}
        </div>
    );
};

const OwnerRow = ({ campaign, onUpdated }) => {
    const [staff, setStaff] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/campaigns/meta/staff-options").then(({ data }) => setStaff(data.staff)).catch(() => setStaff([]));
    }, []);
    const assign = async (staffId) => {
        setBusy(true);
        try {
            await api.post(`/admin/campaigns/${campaign.id}/assign`, { staff_id: staffId || null });
            toast.success(staffId ? "Owner reassigned." : "Owner cleared.");
            await onUpdated();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not reassign."); }
        finally { setBusy(false); }
    };
    return (
        <div className="border border-white/10 p-3 flex flex-wrap items-center gap-3" data-testid="pipeline-owner-row">
            <UserCircle size={15} className="text-[#FF5A00] shrink-0" />
            <div className="flex-1 min-w-[140px]">
                <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">Campaign owner</div>
                <div className="text-white text-sm" data-testid="pipeline-owner-name">{campaign.owner_name || "Unassigned"}</div>
            </div>
            <select value={campaign.owner_id || ""} disabled={busy || !staff}
                onChange={(e) => assign(e.target.value)} data-testid="pipeline-owner-select"
                className="bg-[#1B2E55] border border-white/10 px-2 py-1.5 text-xs text-white max-w-[220px]">
                <option value="">Unassigned</option>
                {(staff || []).map((s) => (
                    <option key={s.id} value={s.id}>{`${s.name || s.email} · ${(s.role || "").replace(/_/g, " ")}`}</option>
                ))}
            </select>
        </div>
    );
};

const CampaignThread = ({ campaignId }) => {
    const [msgs, setMsgs] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    useEffect(() => {
        api.get(`/campaigns/${campaignId}/messages`).then(({ data }) => setMsgs(data.messages)).catch(() => setMsgs([]));
    }, [campaignId]);
    const send = async () => {
        const t = text.trim();
        if (!t) return;
        setSending(true);
        try {
            const { data } = await api.post(`/campaigns/${campaignId}/messages`, { text: t });
            setMsgs((m) => [...(m || []), data]);
            setText("");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not post message."); }
        finally { setSending(false); }
    };
    return (
        <div data-testid="pipeline-thread">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2 flex items-center gap-2">
                <MessageSquare size={11} className="text-[#FF5A00]" /> / Team thread · internal only
            </div>
            {!msgs ? <Loader2 size={14} className="animate-spin text-[#FF5A00]" /> : (
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="pipeline-thread-messages">
                    {msgs.length === 0 && <div className="text-xs text-slate-600">No messages yet — start the discussion for this campaign.</div>}
                    {msgs.map((m) => (
                        <div key={m.id} className="border border-white/10 px-3 py-2">
                            <div className="flex items-center gap-2 text-[10px] font-mono-tech text-slate-500">
                                <span className="text-white">{m.author_name}</span>
                                <span className="uppercase tracking-widest">{(m.author_role || "").replace(/_/g, " ")}</span>
                                <span>{fmtLocal(m.at)}</span>
                            </div>
                            <div className="text-xs text-slate-300 mt-1 whitespace-pre-wrap">{m.text}</div>
                        </div>
                    ))}
                </div>
            )}
            <div className="mt-2 flex gap-1.5">
                <input value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Message the team about this campaign…"
                    data-testid="pipeline-thread-input"
                    className="flex-1 bg-[#1B2E55] border border-white/10 text-white px-3 py-2 text-xs min-w-0 placeholder:text-slate-600" />
                <button onClick={send} disabled={sending || !text.trim()} data-testid="pipeline-thread-send"
                    className="bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 text-[9px] uppercase tracking-widest inline-flex items-center gap-1">
                    <Send size={11} /> Send
                </button>
            </div>
        </div>
    );
};

const audFmt = (cents) => `$${((cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;

const QuoteVersions = ({ campaignId }) => {
    const [versions, setVersions] = useState(null);
    const [access, setAccess] = useState(null);
    const [discount, setDiscount] = useState("");
    const [applyRules, setApplyRules] = useState(true);
    const [busy, setBusy] = useState(false);
    const load = () => api.get(`/contracts/campaign/${campaignId}/versions`).then(({ data }) => setVersions(data.versions)).catch(() => setVersions([]));
    useEffect(() => {
        load();
        api.get("/admin/my-access").then(({ data }) => setAccess(data)).catch(() => setAccess({ permissions: [] }));
    }, [campaignId]);
    if (!versions || versions.length === 0) return null;
    const canRevise = access?.permissions?.includes("finance.manage");
    const current = versions.find((v) => v.is_current) || versions[0];
    const revise = async () => {
        setBusy(true);
        try {
            await api.post(`/contracts/${current.id}/revise`, {
                manual_discount_cents: Math.round(parseFloat(discount || "0") * 100),
                apply_pricing_rules: applyRules,
            });
            toast.success("New quote version created — previous versions kept.");
            setDiscount("");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not revise quote."); }
        finally { setBusy(false); }
    };
    return (
        <div data-testid="pipeline-quote-versions">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2 flex items-center gap-2">
                <Layers size={11} className="text-[#FF5A00]" /> / Quote versions ({versions.length})
            </div>
            <div className="space-y-1.5">
                {versions.map((v) => (
                    <div key={v.id} className={`flex flex-wrap items-center gap-3 border px-3 py-2 text-xs ${v.is_current ? "border-[#FF5A00]/40" : "border-white/10 opacity-70"}`}
                        data-testid={`quote-version-row-${v.version}`}>
                        <span className="font-mono-tech text-white">{v.contract_number}</span>
                        <span className="px-1.5 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border border-white/15 text-slate-400">v{v.version}</span>
                        {v.is_current && <span className="px-1.5 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border border-emerald-400/40 text-emerald-300">current</span>}
                        <span className="text-slate-500">{v.issue_date}</span>
                        {(v.discount_cents || 0) > 0 && <span className="text-amber-300">−{audFmt(v.discount_cents)} disc</span>}
                        <span className="ml-auto text-white">{audFmt(v.total_cents)}</span>
                    </div>
                ))}
            </div>
            {canRevise && (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="quote-revise-controls">
                    <input type="number" min="0" step="1" value={discount} onChange={(e) => setDiscount(e.target.value)}
                        placeholder="Manual discount $ (optional)" data-testid="quote-revise-discount"
                        className="bg-[#1B2E55] border border-white/10 text-white px-3 py-2 text-xs w-52 placeholder:text-slate-600" />
                    <label className="flex items-center gap-1.5 text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 cursor-pointer">
                        <input type="checkbox" checked={applyRules} onChange={(e) => setApplyRules(e.target.checked)} data-testid="quote-revise-rules" />
                        Apply pricing rules
                    </label>
                    <button onClick={revise} disabled={busy} data-testid="quote-revise-btn"
                        className="bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 py-2 text-[9px] font-semibold uppercase tracking-widest">
                        {busy ? <Loader2 size={11} className="animate-spin" /> : "Revise quote"}
                    </button>
                </div>
            )}
        </div>
    );
};

const PROOF_KINDS = [["photo", "Photo"], ["playback_log", "Playback log"], ["supplier_report", "Supplier report"]];

const ProofPanel = ({ campaign, onUpdated }) => {
    const [data, setData] = useState(null);
    const [kind, setKind] = useState("photo");
    const [summary, setSummary] = useState("");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const load = () => api.get(`/proof/${campaign.id}`).then(({ data }) => setData(data)).catch(() => setData({ records: [], missing_proof: false }));
    useEffect(() => { load(); }, [campaign.id]);
    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 15 * 1024 * 1024) { toast.error("Proof file exceeds the 15 MB limit."); return; }
        setBusy(true);
        try {
            const b64 = await new Promise((res, rej) => {
                const r = new FileReader();
                r.onload = () => res(r.result.split(",")[1]);
                r.onerror = rej;
                r.readAsDataURL(f);
            });
            await api.post(`/proof/${campaign.id}/upload`, { kind, filename: f.name, content_type: f.type || "application/octet-stream", content_b64: b64 });
            toast.success("Proof attached.");
            load(); await onUpdated();
        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed."); }
        finally { setBusy(false); }
    };
    const addSummary = async () => {
        if (!summary.trim()) return;
        setBusy(true);
        try {
            await api.post(`/proof/${campaign.id}/summary`, { text: summary.trim() });
            toast.success("Campaign summary recorded.");
            setSummary("");
            load(); await onUpdated();
        } catch (err) { toast.error(err.response?.data?.detail || "Could not save summary."); }
        finally { setBusy(false); }
    };
    if (!data) return null;
    return (
        <div data-testid="pipeline-proof-panel">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2 flex items-center gap-2">
                <ClipboardCheck size={11} className="text-[#FF5A00]" /> / Proof of play
            </div>
            {data.missing_proof && (
                <div className="mb-2 flex items-center gap-2 border border-red-400/40 bg-red-500/5 px-3 py-2 text-[11px] text-red-300" data-testid="proof-missing-warning">
                    <AlertTriangle size={12} /> Campaign is marked done with no proof-of-play attached.
                </div>
            )}
            <div className="space-y-1.5 mb-2">
                {data.records.length === 0 && <div className="text-xs text-slate-600">No verification records yet.</div>}
                {data.records.map((r) => (
                    <div key={r.id} className="border border-white/10 px-3 py-2 text-xs" data-testid={`proof-record-${r.id}`}>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="px-1.5 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border border-white/15 text-slate-400">{r.kind.replace("_", " ")}</span>
                            {r.filename ? (
                                <a href={`${api.defaults.baseURL}/proof/file/${r.id}/download`} target="_blank" rel="noreferrer"
                                    className="text-white hover:text-[#FF5A00] inline-flex items-center gap-1">
                                    <Download size={10} className="text-[#FF5A00]" /> {r.filename}
                                </a>
                            ) : <span className="text-white">Summary</span>}
                            <span className="ml-auto text-[10px] font-mono-tech text-slate-500">{r.uploaded_by_name} · {(r.at || "").slice(0, 10)}</span>
                        </div>
                        {r.note && <div className="mt-1 text-slate-400">{r.note}</div>}
                    </div>
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="proof-kind-select"
                    className="bg-[#1B2E55] border border-white/10 text-white px-2 py-2 text-xs">
                    {PROOF_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="proof-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="proof-upload-btn"
                    className="border border-white/15 hover:border-[#FF5A00]/60 text-slate-300 px-3 py-2 text-[9px] uppercase tracking-widest">
                    {busy ? <Loader2 size={11} className="animate-spin" /> : "Attach file"}
                </button>
            </div>
            <div className="mt-2 flex gap-1.5">
                <input value={summary} onChange={(e) => setSummary(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSummary()}
                    placeholder="Campaign summary note…" data-testid="proof-summary-input"
                    className="flex-1 bg-[#1B2E55] border border-white/10 text-white px-3 py-2 text-xs min-w-0 placeholder:text-slate-600" />
                <button onClick={addSummary} disabled={busy || !summary.trim()} data-testid="proof-summary-add"
                    className="bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 text-[9px] uppercase tracking-widest">Add</button>
            </div>
        </div>
    );
};

// ---------- Phase 5 · Contract panel ----------
const ContractPanel = ({ campaignId }) => {
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/contracts", { params: { campaign_id: campaignId } });
            setContract((data || []).find((c) => c.is_current !== false) || data?.[0] || null);
        } catch (e) {
            /* silent */
        } finally { setLoading(false); }
    }, [campaignId]);

    useEffect(() => { load(); }, [load]);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/campaigns/${campaignId}/contracts`);
            setContract(data);
            toast.success("Contract created.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not generate contract.");
        } finally { setBusy(false); }
    };

    const portalUrl = useMemo(
        () => contract ? `${window.location.origin}/portal/contract/${contract.access_token}` : "",
        [contract]
    );

    const copyLink = async () => {
        if (!portalUrl) return;
        try { await navigator.clipboard.writeText(portalUrl); setCopied(true); setTimeout(() => setCopied(false), 1600); }
        catch { toast.error("Copy failed."); }
    };

    const [emailing, setEmailing] = useState(false);
    const sendEmail = async () => {
        if (!contract) return;
        setEmailing(true);
        try {
            const { data } = await api.post(`/contracts/${contract.id}/send`);
            toast.success(`Emailed to client · ${data?.status || "sent"}.`);
            setContract((c) => c ? { ...c, status: data?.status || c.status } : c);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Email send failed.");
        } finally { setEmailing(false); }
    };

    const pdfHref = contract
        ? `${process.env.REACT_APP_BACKEND_URL}/api/contracts/public/${contract.access_token}/pdf`
        : null;

    return (
        <div data-testid="pipeline-contract-panel">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">/ Contract</div>
            {loading ? (
                <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
            ) : !contract ? (
                <button onClick={generate} disabled={busy}
                    data-testid="pipeline-contract-generate"
                    className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF5A00] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">
                    {busy ? <><Loader2 size={12} className="animate-spin" /> Working…</> : <><FileText size={12} /> Generate contract</>}
                </button>
            ) : (
                <div className="border border-white/10 p-4 space-y-3" data-testid="pipeline-contract-card">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-white text-sm font-medium">{contract.contract_number}</div>
                            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mt-0.5">
                                Total {(contract.total_cents / 100).toLocaleString("en-AU", { style: "currency", currency: "AUD" })}
                            </div>
                        </div>
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-mono-tech uppercase tracking-widest border ${
                            contract.status === "signed" ? "border-emerald-400/50 text-emerald-300" :
                            contract.status === "sent" ? "border-sky-400/50 text-sky-300" :
                            "border-[#FF5A00]/50 text-[#FF5A00]"
                        }`}>
                            {contract.status}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a href={pdfHref} target="_blank" rel="noopener noreferrer"
                            data-testid="pipeline-contract-pdf"
                            className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 text-slate-300 hover:text-white px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest">
                            <Download size={11} /> PDF
                        </a>
                        <button onClick={copyLink}
                            data-testid="pipeline-contract-copy"
                            className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 text-slate-300 hover:text-white px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest">
                            {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Client link</>}
                        </button>
                        <a href={`/portal/contract/${contract.access_token}`} target="_blank" rel="noopener noreferrer"
                            data-testid="pipeline-contract-open"
                            className="inline-flex items-center gap-1.5 border border-[#FF5A00]/50 hover:border-[#FF5A00] text-[#FF5A00] px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest">
                            <FileText size={11} /> Open portal
                        </a>
                        <button onClick={sendEmail} disabled={emailing || !contract.email}
                            data-testid="pipeline-contract-send"
                            title={contract.email ? `Email link to ${contract.email}` : "No client email on file"}
                            className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 disabled:opacity-40 text-slate-300 hover:text-white px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest">
                            {emailing ? <><Loader2 size={11} className="animate-spin" /> Sending</> : <><Mail size={11} /> Email link</>}
                        </button>
                    </div>
                    {contract.status === "signed" && contract.signature && (
                        <div className="border-t border-white/5 pt-3 text-[10px] font-mono-tech uppercase tracking-widest text-emerald-300 flex flex-wrap gap-3">
                            <span>Signed by {contract.signature.full_name}</span>
                            <span>{new Date(contract.signature.signed_at).toLocaleString("en-AU")}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
